/**
 * Credit Balance Hook
 * 
 * Provides real-time access to user's credit balance with auto-refresh.
 * Subscribes to Ably org channel for credit-update so the UI updates when
 * credits are deducted (e.g. after AI Assistant usage).
 */

import { useOrgAblyChannel } from '@/hooks/useOrgAblyChannel'
import { useQuery } from '@tanstack/react-query'
import { useAuthContext } from '@/context'
import { imageCreditsEnabledForUser } from '@/lib/imageCreditsAccess'
import { NodeAxios } from '@/services/api'

export interface CreditBalance {
    ai: {
        total: number
        used: number
        remaining: number
        warningLevel: 'ok' | 'warning' | 'critical'
    }
    images: {
        total: number
        used: number
        remaining: number
        warningLevel: 'ok' | 'warning' | 'critical'
    }
    lastUpdated: Date
}

export interface UseCreditBalanceReturn {
    credits: {
        ai: number
        images: number
    }
    balance: CreditBalance | null
    isLoading: boolean
    isError: boolean
    error: Error | null
    refetch: () => void
    hasSufficient: (required: { ai?: number; images?: number }) => boolean
    warnings: {
        ai: 'ok' | 'low' | 'critical' | 'none'
        images: 'ok' | 'low' | 'critical' | 'none'
    }
}

export function useCreditBalance(): UseCreditBalanceReturn {
    const { user } = useAuthContext()
    const imageCreditsEnabled = imageCreditsEnabledForUser(user)

    const { data, isLoading, isError, error, refetch } = useQuery<{ credits: CreditBalance }>({
        queryKey: ['credits', 'balance'],
        queryFn: async () => {
            const response = await NodeAxios.get('/credits/balance')
            return response as any
        },
        refetchInterval: 30000, // Refetch every 30 seconds
        refetchOnWindowFocus: true,
        staleTime: 15000, // Consider data stale after 15 seconds
    })

    useOrgAblyChannel('credit-update', () => {
        refetch()
    })

    const balance = data?.credits || null

    // 🆕 Floor credits at 0 for display (backend may go slightly negative with overdraft)
    const credits = {
        ai: Math.max(balance?.ai.remaining || 0, 0),
        images: Math.max(balance?.images.remaining || 0, 0),
    }

    const hasSufficient = (required: { ai?: number; images?: number }) => {
        const aiSufficient = required.ai ? credits.ai >= required.ai : true
        const imagesSufficient =
            !imageCreditsEnabled || !required.images ? true : credits.images >= required.images
        return aiSufficient && imagesSufficient
    }

    const getWarningLevel = (remaining: number, type: 'ai' | 'images'): 'ok' | 'low' | 'critical' | 'none' => {
        if (remaining === 0) return 'none'
        if (type === 'ai') {
            if (remaining < 10) return 'critical'
            if (remaining < 50) return 'low'
            return 'ok'
        } else {
            if (remaining < 5) return 'critical'
            if (remaining < 20) return 'low'
            return 'ok'
        }
    }

    const warnings = {
        ai: getWarningLevel(credits.ai, 'ai'),
        images: imageCreditsEnabled ? getWarningLevel(credits.images, 'images') : ('ok' as const),
    }

    return {
        credits,
        balance,
        isLoading,
        isError,
        error: error as Error | null,
        refetch,
        hasSufficient,
        warnings,
    }
}

